// ─────────────────────────────────────────────────────────────────
// Verdict de posture d'un compte, et pertinence des sections.
//
// Sans React ni Redux : c'est la règle métier de la fiche, et elle doit être
// vérifiable sans monter un composant.
//
// Le calibrage vient de mesures sur le parc (2026-09-10, 4 339 comptes non
// supprimés). La leçon de la fiche appareil s'applique : un critère vrai pour
// la majorité du parc n'informe pas. « Sans MFA » concerne 2 794 comptes, dont
// 2 396 invités sans licence ni appareil — inutilisable comme alerte. Croisé
// avec un privilège ou une licence, il isole 14 et 107 comptes.
// ─────────────────────────────────────────────────────────────────

export const JOURS_INACTIVITE = 90;

const GRAVITE = { critique: 3, surveiller: 2, dormant: 1, sain: 0 };

function joursDepuis(date) {
    if (!date) return null;
    const valeur = new Date(date);
    if (Number.isNaN(valeur.getTime())) return null;
    return Math.floor((Date.now() - valeur.getTime()) / 86400000);
}

export function calculerVerdict(utilisateur) {
    if (!utilisateur) return { ton: 'neutral', niveau: 'sain', mot: 'Inconnu', motifs: [] };

    // Un compte absent du dernier balayage Entra ne s'évalue pas, il se signale.
    //
    // Tout le reste de la fiche est un instantané figé au dernier passage où
    // Entra le connaissait : 451 des 672 comptes hors annuaire portent encore
    // accountEnabled à true et 406 gardent des licences inscrites. Rendre un
    // verdict de posture sur ces valeurs ferait passer un compte parti pour un
    // compte à traiter — et masquerait le seul fait qui compte ici.
    if (utilisateur.isDeleted === true) {
        return {
            ton: 'warn',
            niveau: 'absent',
            mot: 'Hors annuaire',
            motifs: ['compte absent du dernier balayage Entra', 'données figées'],
        };
    }

    const roles = utilisateur.roles || [];
    const licences = utilisateur.licenses || [];
    const mfa = utilisateur.authMethods?.isMfaRegistered === true;
    const actif = utilisateur.accountEnabled === true;
    const invite = utilisateur.userType === 'Guest';
    const derniereConnexion = utilisateur.signInActivity?.lastSignInDateTime || null;
    const inactifDepuis = joursDepuis(derniereConnexion);

    const constats = [];

    // Le plus grave : un compte privilégié sans authentification forte.
    // 14 comptes sur le parc, dont 9 actifs.
    if (roles.length > 0 && !mfa) {
        constats.push({ niveau: 'critique', texte: `${roles.length} rôle${roles.length > 1 ? 's' : ''} privilégié${roles.length > 1 ? 's' : ''} sans MFA` });
    }

    // Un compte licencié est un compte qui sert : l'absence de MFA y est
    // actionnable, contrairement à un invité sans licence.
    if (actif && licences.length > 0 && !mfa) {
        constats.push({ niveau: 'surveiller', texte: 'compte licencié sans MFA' });
    }

    // Une licence sur un compte fermé est à la fois une dépense et une surface.
    if (!actif && licences.length > 0) {
        constats.push({ niveau: 'surveiller', texte: `désactivé mais ${licences.length} licence${licences.length > 1 ? 's' : ''} attribuée${licences.length > 1 ? 's' : ''}` });
    }

    if (actif && inactifDepuis !== null && inactifDepuis > JOURS_INACTIVITE) {
        constats.push({ niveau: 'surveiller', texte: `aucune connexion depuis ${inactifDepuis} jours` });
    }

    // Un invité qui ne s'est jamais connecté : 782 comptes. Ce n'est pas une
    // faille, c'est un compte à fermer.
    if (invite && !derniereConnexion) {
        constats.push({ niveau: 'dormant', texte: 'invité jamais connecté' });
    }

    if (constats.length === 0) {
        return {
            ton: 'ok',
            niveau: 'sain',
            mot: 'Sain',
            motifs: mfa ? ['MFA enregistré'] : [],
        };
    }

    const pire = constats.reduce((a, b) => (GRAVITE[b.niveau] > GRAVITE[a.niveau] ? b : a));
    const MOTS = { critique: 'Critique', surveiller: 'À surveiller', dormant: 'Compte dormant' };
    const TONS = { critique: 'danger', surveiller: 'warn', dormant: 'accent' };

    return {
        ton: TONS[pire.niveau],
        niveau: pire.niveau,
        mot: MOTS[pire.niveau],
        motifs: constats.map((c) => c.texte),
    };
}

// Les attributs d'extension réellement remplis. Sur les quinze déclarés, sept
// seulement portent une valeur quelque part dans le parc : afficher les quinze
// donnerait huit lignes vides sur chaque fiche.
export function attributsRenseignes(utilisateur) {
    const source = utilisateur?.onPremisesExtensionAttributes || {};
    return Object.entries(source)
        .filter(([cle, valeur]) => cle.startsWith('extensionAttribute') && valeur)
        .map(([cle, valeur]) => ({ numero: Number(cle.replace('extensionAttribute', '')), valeur }))
        .sort((a, b) => a.numero - b.numero);
}

// Une section vide ne se vaut pas selon le type de compte.
//
// Pour un MEMBRE, « aucun appareil » est un constat : la section reste, avec son
// état vide. Pour un INVITÉ, c'est structurel — aucun des 2 402 invités n'a
// d'appareil, aucun n'a de licence, un seul a une licence — et huit sections
// vides sur dix font douter de la donnée au lieu d'informer.
const TOUJOURS = ['apercu', 'compte'];
const STRUCTURANTES_MEMBRE = ['organisation', 'groupes', 'applications', 'appareils', 'licences'];

export function panneauPertinent(cle, { invite, compteurs }) {
    if (TOUJOURS.includes(cle)) return true;
    if ((compteurs[cle] || 0) > 0) return true;
    return !invite && STRUCTURANTES_MEMBRE.includes(cle);
}
